import React from 'react';
import block from 'bem-cn-lite';
import {Button} from '@yandex-data-ui/common';
import {HTML} from '@doc-tools/components';
import {HeightCalculator, Arrow, ArrowDirection} from '@yandex-data-ui/page-constructor';

import withTranslation, {WithTranslationProps} from 'hoc/withTranslation';
import {QuotesProps as QuotesParams, QuoteItem} from 'units/constructor-local/models';
import AuthorBlock, {AuthorBlockType} from 'components/AuthorBlock/AuthorBlock';
import Image from 'components/Image/Image';

import './Quotes.scss';

const b = block('quotes-block');
const MIN_HEIGHT = 360;

type QuotesProps = QuotesParams & WithTranslationProps;

interface QuotesState {
    selected: number;
    height: number;
}

export class Quotes extends React.Component<QuotesProps, QuotesState> {
    state = {
        height: MIN_HEIGHT,
        selected: 0,
    };

    render() {
        const {items} = this.props;
        const {selected, height} = this.state;

        if (!items.length) {
            return null;
        }
        return (
            <div className={b()}>
                {this.renderLogos(selected)}
                <div className={b('relative')} style={{height}}>
                    {this.renderArrow()}
                    {this.renderCard(items[selected])}
                    {this.renderArrow('right')}
                    <HeightCalculator onCalculate={this.handleHeightCalculation}>
                        {items.map((item) => this.renderCardContent(item))}
                    </HeightCalculator>
                </div>
            </div>
        );
    }

    private renderLogos(selected: number) {
        return (
            <div className={b('logo-container')}>
                {this.props.items.map(({logo}, index) => (
                    <img
                        key={logo}
                        src={logo}
                        className={b('logo', {inactive: index !== selected})}
                        onClick={() => this.handleLogoClick(index)}
                    />
                ))}
            </div>
        );
    }

    private renderCard(item: QuoteItem) {
        const {theme, color, image} = item;

        return (
            <div className={b('card', {theme})} style={color ? {backgroundColor: color} : {}}>
                {this.renderCardContent(item)}
                {this.renderCardImage(image)}
            </div>
        );
    }

    private renderCardImage(imageSrc: string) {
        return (
            <div className={b('card-image-wrapper')}>
                <Image src={imageSrc} className={b('card-image')} />
            </div>
        );
    }

    private renderArrow(direction: ArrowDirection = 'left') {
        return (
            <div className={b('arrow', {direction})}>
                <Arrow direction={direction} onClick={this.handleArrowClick} />
            </div>
        );
    }

    private renderCardContent({text, author, url, theme}: QuoteItem) {
        const {i18nK} = this.props;

        return (
            <div key={text} className={b('card-content-wrapper')}>
                <div className={b('card-content')}>
                    <span className={b('card-text')}>
                        <HTML>{text}</HTML>
                    </span>
                    {(author || url) && (
                        <div className={b('card-author-wrapper')}>
                            {author && (
                                <AuthorBlock
                                    className={b('author', {theme})}
                                    author={author}
                                    type={AuthorBlockType.Line}
                                />
                            )}
                            {url && (
                                <Button
                                    view="outlined"
                                    size="l"
                                    href={url}
                                    className={b('link-button', {theme})}
                                >
                                    {i18nK('constructor_see_also')}
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    private handleLogoClick(selected: number) {
        this.setState({selected});
    }

    private handleArrowClick = (direction: ArrowDirection) => {
        const {selected} = this.state;
        const {items} = this.props;
        let newSelected;

        if (direction === 'left') {
            newSelected = selected ? selected - 1 : items.length - 1;
        } else {
            newSelected = selected === items.length - 1 ? 0 : selected + 1;
        }

        this.setState({selected: newSelected});
    };

    private handleHeightCalculation = (height: number) => {
        this.setState({height: Math.max(height, MIN_HEIGHT)});
    };
}

export default withTranslation('constructor')(Quotes);
